import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Sidebar } from "./components/Sidebar";
import { TopBar } from "./components/TopBar";
import { Dashboard } from "./components/Dashboard";
import { BusinessDetail } from "./components/BusinessDetail";
import { CompanyDetail } from "./components/CompanyDetail";
import { ProjectDetail } from "./components/ProjectDetail";
import { BusinessForm } from "./components/forms/BusinessForm";
import { CompanyForm } from "./components/forms/CompanyForm";
import { ProjectForm } from "./components/forms/ProjectForm";
import { SearchResults } from "./components/SearchResults";
import { TeamMemberManager } from "./components/TeamMemberManager";
import { UserManager } from "./components/UserManager";
import { NotificationCenter } from "./components/NotificationCenter";
import { TaskWorkspace } from "./components/TaskWorkspace";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 15000,
});

function LoginGate({ onLogin, loading }) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    await onLogin({
      username: username.trim(),
      password,
      totp_code: totpCode.trim(),
    });
  };

  return (
    <div className="login-screen">
      <div className="login-panel">
        <div className="login-panel-header">
          <p className="login-eyebrow">Restricted Access</p>
          <h1>Batsho Management App</h1>
          <p className="login-copy">Sign in with your password and six-digit authenticator code to unlock the dashboard.</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <label className="login-field">
            <span>Username</span>
            <input autoComplete="username" className="login-input" disabled={loading} onChange={(event) => setUsername(event.target.value)} type="text" value={username} />
          </label>
          <label className="login-field">
            <span>Password</span>
            <input autoComplete="current-password" className="login-input" disabled={loading} onChange={(event) => setPassword(event.target.value)} type="password" value={password} />
          </label>
          <label className="login-field">
            <span>Authenticator Code</span>
            <input autoComplete="one-time-code" className="login-input" disabled={loading} inputMode="numeric" maxLength={6} onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" type="text" value={totpCode} />
          </label>
          <button className="login-submit" disabled={loading} type="submit">{loading ? "Checking..." : "Sign In"}</button>
        </form>
      </div>
    </div>
  );
}

function SharedProjectView({ payload }) {
  if (!payload) {
    return <div className="empty-state"><div className="text-lg font-mono">Loading shared project...</div></div>;
  }

  const project = payload.project || {};
  const tasks = project.tasks || [];

  return (
    <div className="content-area">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="card p-6">
          <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-zinc-900">{project.name || "Shared Project"}</h1>
          <p className="mt-2 text-sm text-zinc-500">{payload.company?.name} / {payload.business?.name}</p>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-zinc-600">
            <span>Status: {project.status || "Unknown"}</span>
            <span>Priority: {project.priority || "Unknown"}</span>
            <span>Lead: {project.lead || "Unassigned"}</span>
          </div>
        </div>
        <div className="card p-6">
          <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">Project Tasks</h2>
          <div className="space-y-4">
            {tasks.length === 0 ? <p className="text-sm text-zinc-400">No tasks available.</p> : tasks.map((task) => (
              <div className="border border-zinc-200 bg-white p-4" key={task.id}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-zinc-900">{task.title}</h3>
                    <p className="mt-1 text-sm text-zinc-500">{task.assignee || "Unassigned"} · {task.due_date || "No due date"}</p>
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-400">{task.status}</span>
                </div>
                {task.notes ? <p className="mt-3 text-sm text-zinc-700">{task.notes}</p> : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  const authFlowRef = useRef({ sessionCheckId: 0, loginAttemptId: 0 });
  const publicShareToken = useMemo(() => {
    const match = window.location.pathname.match(/^\/public\/share\/([^/]+)$/);
    return match ? match[1] : null;
  }, []);

  const [hierarchy, setHierarchy] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activity, setActivity] = useState([]);
  const [taskBoard, setTaskBoard] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [publicShare, setPublicShare] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [view, setView] = useState("dashboard");
  const [taskFilter, setTaskFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState("checking");
  const [authLoading, setAuthLoading] = useState(false);
  const [aiBriefLoading, setAiBriefLoading] = useState(false);

  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [showBusinessForm, setShowBusinessForm] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showTeamMemberManager, setShowTeamMemberManager] = useState(false);
  const [showUserManager, setShowUserManager] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const resetWorkspace = useCallback(() => {
    setHierarchy([]);
    setDashboardData(null);
    setTeamMembers([]);
    setUsers([]);
    setNotifications([]);
    setActivity([]);
    setTaskBoard([]);
    setCurrentUser(null);
    setSelectedCompanyId(null);
    setSelectedBusinessId(null);
    setSelectedProjectId(null);
    setSelectedTaskId(null);
    setView("dashboard");
    setTaskFilter("all");
    setSearchQuery("");
    setSearchResults(null);
    setLoading(false);
  }, []);

  const handleUnauthorized = useCallback(() => {
    setAuthStatus("unauthenticated");
    resetWorkspace();
  }, [resetWorkspace]);

  const isUnauthorized = (error) => error?.response?.status === 401;

  const fetchHierarchy = useCallback(async () => {
    const response = await apiClient.get("/hierarchy");
    setHierarchy(response.data);
    return response.data;
  }, []);

  const fetchDashboard = useCallback(async () => {
    const response = await apiClient.get("/dashboard");
    setDashboardData(response.data);
    return response.data;
  }, []);

  const fetchTeamMembers = useCallback(async () => {
    const response = await apiClient.get("/team-members");
    setTeamMembers(response.data);
    return response.data;
  }, []);

  const fetchUsers = useCallback(async () => {
    const response = await apiClient.get("/users");
    setUsers(response.data);
    return response.data;
  }, []);

  const fetchNotifications = useCallback(async () => {
    const response = await apiClient.get("/notifications");
    setNotifications(response.data);
    return response.data;
  }, []);

  const fetchActivity = useCallback(async () => {
    const response = await apiClient.get("/activity");
    setActivity(response.data);
    return response.data;
  }, []);

  const fetchTaskBoard = useCallback(async () => {
    const response = await apiClient.get("/tasks/board");
    setTaskBoard(response.data);
    return response.data;
  }, []);

  const loadData = useCallback(async (user) => {
    setLoading(true);
    try {
      const requests = [fetchHierarchy(), fetchDashboard(), fetchTeamMembers(), fetchNotifications(), fetchActivity(), fetchTaskBoard()];
      if (user?.role === "admin") {
        requests.push(fetchUsers());
      } else {
        setUsers([]);
      }
      await Promise.all(requests);
    } finally {
      setLoading(false);
    }
  }, [fetchActivity, fetchDashboard, fetchHierarchy, fetchNotifications, fetchTaskBoard, fetchTeamMembers, fetchUsers]);

  const checkSession = useCallback(async () => {
    if (publicShareToken) return;
    const sessionCheckId = authFlowRef.current.sessionCheckId + 1;
    authFlowRef.current.sessionCheckId = sessionCheckId;
    setAuthStatus("checking");
    try {
      const response = await apiClient.get("/auth/session");
      if (
        authFlowRef.current.sessionCheckId !== sessionCheckId ||
        authFlowRef.current.loginAttemptId > 0
      ) {
        return;
      }
      setCurrentUser(response.data);
      setAuthStatus("authenticated");
      await loadData(response.data);
    } catch (error) {
      if (authFlowRef.current.sessionCheckId !== sessionCheckId) {
        return;
      }
      if (!isUnauthorized(error)) {
        console.error("Error checking session:", error);
      }
      setAuthStatus("unauthenticated");
      resetWorkspace();
    } finally {
      authFlowRef.current.sessionCheckId = Math.max(authFlowRef.current.sessionCheckId, sessionCheckId);
    }
  }, [loadData, publicShareToken, resetWorkspace]);

  useEffect(() => {
    if (!publicShareToken) {
      checkSession();
      return;
    }
    (async () => {
      try {
        const response = await apiClient.get(`/public/share/${publicShareToken}`);
        setPublicShare(response.data);
      } catch (error) {
        console.error("Error loading shared project:", error);
        setPublicShare({ project: { name: "Shared project unavailable", tasks: [] }, company: {}, business: {} });
      }
    })();
  }, [publicShareToken]);

  const runAction = async (callback, fallbackMessage = "Request failed") => {
    try {
      return await callback();
    } catch (error) {
      if (isUnauthorized(error)) {
        handleUnauthorized();
        return null;
      }
      console.error(error);
      toast.error(error?.response?.data?.detail || fallbackMessage);
      throw error;
    }
  };

  const handleLogin = async (credentials) => {
    authFlowRef.current.loginAttemptId += 1;
    setAuthLoading(true);
    try {
      const response = await apiClient.post("/auth/login", credentials);
      setCurrentUser(response.data);
      setAuthStatus("authenticated");
      toast.success("Signed in successfully");
    } catch (error) {
      console.error("Error signing in:", error);
      setAuthStatus("unauthenticated");
      toast.error(error?.response?.data?.detail || "Sign in failed");
    } finally {
      authFlowRef.current.loginAttemptId = 0;
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    if (authStatus !== "authenticated" || !currentUser) {
      return;
    }

    (async () => {
      try {
        await loadData(currentUser);
      } catch (error) {
        if (isUnauthorized(error)) {
          handleUnauthorized();
          return;
        }
        console.error("Error loading authenticated workspace:", error);
        toast.error("Signed in, but some dashboard data could not be loaded.");
      }
    })();
  }, [authStatus, currentUser, handleUnauthorized, loadData]);

  const handleLogout = useCallback(async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      handleUnauthorized();
    }
  }, [handleUnauthorized]);

  const handleGenerateAiBrief = async () => {
    setAiBriefLoading(true);
    try {
      const response = await apiClient.get("/reports/smart?include_ai=true");
      setDashboardData((current) => (current ? { ...current, smart_report: response.data } : current));
      if (response.data.ai_brief) {
        toast.success("AI brief generated");
      } else if (response.data.ai_brief_error) {
        toast.error(response.data.ai_brief_error);
      }
    } catch (error) {
      if (isUnauthorized(error)) {
        handleUnauthorized();
        return;
      }
      console.error(error);
      toast.error("Failed to generate AI brief");
    } finally {
      setAiBriefLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults(null);
      setView("dashboard");
      return;
    }
    await runAction(async () => {
      const response = await apiClient.get(`/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
      setView("search");
    }, "Search failed");
  };

  const resetSearch = () => {
    setSearchQuery("");
    setSearchResults(null);
  };

  const handleSelectCompany = (companyId) => {
    setSelectedCompanyId(companyId);
    setSelectedBusinessId(null);
    setSelectedProjectId(null);
    setSelectedTaskId(null);
    setView("company");
    setSidebarOpen(false);
    resetSearch();
  };

  const handleSelectBusiness = (businessId, companyId) => {
    setSelectedCompanyId(companyId);
    setSelectedBusinessId(businessId);
    setSelectedProjectId(null);
    setSelectedTaskId(null);
    setView("business");
    setSidebarOpen(false);
    resetSearch();
  };

  const handleSelectProject = (projectId, businessId, companyId, taskId = null) => {
    setSelectedCompanyId(companyId);
    setSelectedBusinessId(businessId);
    setSelectedProjectId(projectId);
    setSelectedTaskId(taskId);
    setView("project");
    setSidebarOpen(false);
    resetSearch();
  };

  const handleGoToDashboard = () => {
    setSelectedCompanyId(null);
    setSelectedBusinessId(null);
    setSelectedProjectId(null);
    setSelectedTaskId(null);
    setView("dashboard");
    setSidebarOpen(false);
    resetSearch();
  };

  const handleGoToTasks = () => {
    setSelectedCompanyId(null);
    setSelectedBusinessId(null);
    setSelectedProjectId(null);
    setSelectedTaskId(null);
    setView("tasks");
    setSidebarOpen(false);
    resetSearch();
  };

  const handleOpenDashboardProject = (projectId, taskId = null) => {
    for (const company of hierarchy) {
      for (const business of company.businesses || []) {
        const project = (business.projects || []).find((item) => item.id === projectId);
        if (project) {
          handleSelectProject(projectId, business.id, company.id, taskId);
          return;
        }
      }
    }
    toast.error("Project could not be found");
  };

  const postAndReload = async (request, successMessage) => {
    const result = await runAction(request);
    if (successMessage) toast.success(successMessage);
    await loadData(currentUser);
    return result?.data || null;
  };

  const handleCreateUser = (data) => postAndReload(() => apiClient.post("/users", data), "User created");
  const handleUpdateUser = (userId, data) => postAndReload(() => apiClient.put(`/users/${userId}`, data), "User updated");
  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Delete this user?")) return;
    await postAndReload(() => apiClient.delete(`/users/${userId}`), "User deleted");
  };

  const handleMarkNotificationRead = async (notificationId) => {
    await postAndReload(() => apiClient.post(`/notifications/${notificationId}/read`), "Notification updated");
  };

  const handleCreateTeamMember = (data) => postAndReload(() => apiClient.post("/team-members", data), "Team member added");
  const handleUpdateTeamMember = (memberId, data) => postAndReload(() => apiClient.put(`/team-members/${memberId}`, data), "Team member updated");
  const handleDeleteTeamMember = async (memberId) => {
    if (!window.confirm("Delete this team member?")) return;
    await postAndReload(() => apiClient.delete(`/team-members/${memberId}`), "Team member deleted");
  };
  const handleCreateCompany = async (data) => { await postAndReload(() => apiClient.post("/companies", data), "Company created successfully"); setShowCompanyForm(false); };
  const handleUpdateCompany = async (id, data) => { await postAndReload(() => apiClient.put(`/companies/${id}`, data), "Company updated successfully"); setEditingCompany(null); };
  const handleDeleteCompany = async (id) => { if (!window.confirm("Delete this company and all nested businesses/projects?")) return; await postAndReload(() => apiClient.delete(`/companies/${id}`), "Company deleted successfully"); if (selectedCompanyId === id) handleGoToDashboard(); };
  const handleCreateBusiness = async (data) => { await postAndReload(() => apiClient.post("/businesses", data), "Business created successfully"); setShowBusinessForm(false); };
  const handleUpdateBusiness = async (id, data) => { await postAndReload(() => apiClient.put(`/businesses/${id}`, data), "Business updated successfully"); setEditingBusiness(null); };
  const handleDeleteBusiness = async (id) => { if (!window.confirm("Delete this business and all its projects?")) return; await postAndReload(() => apiClient.delete(`/businesses/${id}`), "Business deleted successfully"); if (selectedBusinessId === id) handleSelectCompany(selectedCompanyId); };
  const handleCreateProject = async (data) => { await postAndReload(() => apiClient.post("/projects", data), "Project created successfully"); setShowProjectForm(false); };
  const handleUpdateProject = async (id, data) => { await postAndReload(() => apiClient.put(`/projects/${id}`, data), "Project updated successfully"); setEditingProject(null); };
  const handleDeleteProject = async (id) => { if (!window.confirm("Delete this project?")) return; await postAndReload(() => apiClient.delete(`/projects/${id}`), "Project deleted successfully"); if (selectedProjectId === id) handleSelectBusiness(selectedBusinessId, selectedCompanyId); };
  const handleCreateTask = (projectId, data) => postAndReload(() => apiClient.post(`/projects/${projectId}/tasks`, data), "Task created successfully");
  const handleUpdateTask = (projectId, taskId, data) => postAndReload(() => apiClient.put(`/projects/${projectId}/tasks/${taskId}`, data), "Task updated successfully");
  const handleDeleteTask = async (projectId, taskId) => { if (!window.confirm("Delete this task?")) return; await postAndReload(() => apiClient.delete(`/projects/${projectId}/tasks/${taskId}`), "Task deleted successfully"); };
  const handleCreateSubtask = (projectId, taskId, data) => postAndReload(() => apiClient.post(`/projects/${projectId}/tasks/${taskId}/subtasks`, data), "Subtask created successfully");
  const handleUpdateSubtask = (projectId, taskId, subtaskId, data) => postAndReload(() => apiClient.put(`/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}`, data));
  const handleDeleteSubtask = (projectId, taskId, subtaskId) => postAndReload(() => apiClient.delete(`/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}`), "Subtask deleted successfully");
  const handleCreateComment = (projectId, taskId, data) => postAndReload(() => apiClient.post(`/projects/${projectId}/tasks/${taskId}/comments`, data), "Comment added");
  const handleUpdateComment = (projectId, taskId, commentId, data) => postAndReload(() => apiClient.put(`/projects/${projectId}/tasks/${taskId}/comments/${commentId}`, data), "Comment updated");
  const handleDeleteComment = (projectId, taskId, commentId) => postAndReload(() => apiClient.delete(`/projects/${projectId}/tasks/${taskId}/comments/${commentId}`), "Comment deleted");

  const handleUploadAttachment = async (projectId, taskId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    await postAndReload(() => apiClient.post(`/projects/${projectId}/tasks/${taskId}/attachments`, formData, { headers: { "Content-Type": "multipart/form-data" } }), "Attachment uploaded");
  };

  const handleDeleteAttachment = (projectId, taskId, attachmentId) => postAndReload(() => apiClient.delete(`/projects/${projectId}/tasks/${taskId}/attachments/${attachmentId}`), "Attachment deleted");
  const handleCreateShareLink = (projectId) => postAndReload(() => apiClient.post(`/projects/${projectId}/share-links`), "Share link created");
  const handleGenerateProjectSummary = async (projectId) => {
    const result = await runAction(() => apiClient.post(`/projects/${projectId}/ai-summary`), "Failed to generate project summary");
    return result?.data || null;
  };

  const selectedCompany = hierarchy.find((company) => company.id === selectedCompanyId);
  const selectedBusiness = selectedCompany?.businesses?.find((business) => business.id === selectedBusinessId);
  const selectedProject = selectedBusiness?.projects?.find((project) => project.id === selectedProjectId);
  const projectActivity = activity.filter((item) => item.project_id === selectedProjectId);
  const unreadNotificationCount = notifications.filter((item) => !item.is_read).length;
  const canEdit = currentUser?.role === "admin" || currentUser?.role === "manager";
  const canManageTeam = currentUser?.role === "admin";

  const renderContent = () => {
    if (loading) {
      return <div className="empty-state"><div className="text-lg font-mono">Loading...</div></div>;
    }

    if (view === "search" && searchResults) {
      return <SearchResults onSelectBusiness={handleSelectBusiness} onSelectCompany={handleSelectCompany} onSelectProject={handleSelectProject} query={searchQuery} results={searchResults} />;
    }

    if (view === "tasks") {
      return <TaskWorkspace currentUser={currentUser} exportProjectsUrl={`${API}/exports/projects.csv`} exportTasksUrl={`${API}/exports/tasks.csv`} filter={taskFilter} onChangeFilter={setTaskFilter} onOpenTask={handleOpenDashboardProject} tasks={taskBoard} />;
    }

    if (view === "project" && selectedProject) {
      return (
        <ProjectDetail
          activity={projectActivity}
          business={selectedBusiness}
          company={selectedCompany}
          currentUser={currentUser}
          exportProjectsUrl={`${API}/exports/projects.csv`}
          exportTasksUrl={`${API}/exports/tasks.csv`}
          onBack={() => handleSelectBusiness(selectedBusinessId, selectedCompanyId)}
          onClearTaskSelection={() => setSelectedTaskId(null)}
          onCreateComment={handleCreateComment}
          onCreateShareLink={handleCreateShareLink}
          onCreateSubtask={handleCreateSubtask}
          onCreateTask={handleCreateTask}
          onDelete={() => handleDeleteProject(selectedProject.id)}
          onDeleteAttachment={handleDeleteAttachment}
          onDeleteComment={handleDeleteComment}
          onDeleteSubtask={handleDeleteSubtask}
          onDeleteTask={handleDeleteTask}
          onEdit={() => setEditingProject(selectedProject)}
          onGenerateProjectSummary={handleGenerateProjectSummary}
          onUpdateComment={handleUpdateComment}
          onUpdateSubtask={handleUpdateSubtask}
          onUpdateTask={handleUpdateTask}
          onUploadAttachment={handleUploadAttachment}
          project={selectedProject}
          selectedTaskId={selectedTaskId}
          teamMembers={teamMembers}
        />
      );
    }

    if (view === "business" && selectedBusiness) {
      return <BusinessDetail business={selectedBusiness} company={selectedCompany} onAddProject={canEdit ? () => setShowProjectForm(true) : undefined} onBack={() => handleSelectCompany(selectedCompanyId)} onDelete={canEdit ? () => handleDeleteBusiness(selectedBusiness.id) : undefined} onEdit={canEdit ? () => setEditingBusiness(selectedBusiness) : undefined} onSelectProject={(projectId) => handleSelectProject(projectId, selectedBusiness.id, selectedCompany.id)} />;
    }

    if (view === "company" && selectedCompany) {
      return <CompanyDetail company={selectedCompany} onAddBusiness={canEdit ? () => setShowBusinessForm(true) : undefined} onDelete={canEdit ? () => handleDeleteCompany(selectedCompany.id) : undefined} onEdit={canEdit ? () => setEditingCompany(selectedCompany) : undefined} onSelectBusiness={(businessId) => handleSelectBusiness(businessId, selectedCompany.id)} />;
    }

    return <Dashboard aiBriefLoading={aiBriefLoading} data={dashboardData} onGenerateAiBrief={handleGenerateAiBrief} onOpenProject={handleOpenDashboardProject} />;
  };

  if (publicShareToken) {
    return (
      <>
        <Toaster position="top-right" richColors />
        <SharedProjectView payload={publicShare} />
      </>
    );
  }

  if (authStatus !== "authenticated") {
    return (
      <>
        <Toaster position="top-right" richColors />
        <LoginGate loading={authLoading} onLogin={handleLogin} />
      </>
    );
  }

  return (
    <div className="app-container" data-testid="app-container">
      <Toaster position="top-right" richColors />
      <Sidebar
        hierarchy={hierarchy}
        isOpen={sidebarOpen}
        isTaskView={view === "tasks"}
        onGoToDashboard={handleGoToDashboard}
        onGoToTasks={handleGoToTasks}
        onSelectBusiness={handleSelectBusiness}
        onSelectCompany={handleSelectCompany}
        onSelectProject={handleSelectProject}
        selectedBusinessId={selectedBusinessId}
        selectedCompanyId={selectedCompanyId}
        selectedProjectId={selectedProjectId}
      />
      {sidebarOpen ? <button className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} type="button" /> : null}
      <div className="main-panel">
        <button className="session-logout-button" onClick={handleLogout} type="button">Log Out</button>
        <TopBar
          canEdit={canEdit}
          canManageTeam={canManageTeam}
          canManageUsers={currentUser?.role === "admin"}
          hasBusinesses={hierarchy.some((company) => company.businesses?.length > 0)}
          hasCompanies={hierarchy.length > 0}
          notificationCount={unreadNotificationCount}
          onAddBusiness={() => setShowBusinessForm(true)}
          onAddCompany={() => setShowCompanyForm(true)}
          onAddProject={() => setShowProjectForm(true)}
          onAddTeamMember={() => canManageTeam ? setShowTeamMemberManager(true) : null}
          onGoToTasks={handleGoToTasks}
          onOpenNotifications={() => setShowNotifications(true)}
          onOpenUsers={() => setShowUserManager(true)}
          onSearch={handleSearch}
          onToggleSidebar={() => setSidebarOpen((current) => !current)}
          searchQuery={searchQuery}
        />
        <div className="content-area">{renderContent()}</div>
      </div>

      {canEdit ? <CompanyForm onClose={() => setShowCompanyForm(false)} onSubmit={handleCreateCompany} open={showCompanyForm} /> : null}
      {canEdit ? <CompanyForm initialData={editingCompany} isEdit onClose={() => setEditingCompany(null)} onSubmit={(data) => handleUpdateCompany(editingCompany?.id, data)} open={!!editingCompany} /> : null}
      {canEdit ? <BusinessForm companies={hierarchy} onClose={() => setShowBusinessForm(false)} onSubmit={handleCreateBusiness} open={showBusinessForm} preselectedCompanyId={selectedCompanyId} /> : null}
      {canEdit ? <BusinessForm companies={hierarchy} initialData={editingBusiness} isEdit onClose={() => setEditingBusiness(null)} onSubmit={(data) => handleUpdateBusiness(editingBusiness?.id, data)} open={!!editingBusiness} /> : null}
      {canEdit ? <ProjectForm hierarchy={hierarchy} onClose={() => setShowProjectForm(false)} onSubmit={handleCreateProject} open={showProjectForm} preselectedBusinessId={selectedBusinessId} preselectedCompanyId={selectedCompanyId} teamMembers={teamMembers} /> : null}
      {canEdit ? <ProjectForm hierarchy={hierarchy} initialData={editingProject} isEdit onClose={() => setEditingProject(null)} onSubmit={(data) => handleUpdateProject(editingProject?.id, data)} open={!!editingProject} teamMembers={teamMembers} /> : null}
      {canManageTeam ? <TeamMemberManager members={teamMembers} onClose={() => setShowTeamMemberManager(false)} onCreate={handleCreateTeamMember} onDelete={handleDeleteTeamMember} onUpdate={handleUpdateTeamMember} open={showTeamMemberManager} /> : null}
      {currentUser?.role === "admin" ? <UserManager companies={hierarchy} onClose={() => setShowUserManager(false)} onCreate={handleCreateUser} onDelete={handleDeleteUser} onUpdate={handleUpdateUser} open={showUserManager} users={users} /> : null}
      <NotificationCenter notifications={notifications} onClose={() => setShowNotifications(false)} onMarkRead={handleMarkNotificationRead} onOpenTask={handleOpenDashboardProject} open={showNotifications} />
    </div>
  );
}

export default App;
